import {presentationIcons, presentationText, type ToolPresentation} from "@repo/ui/gailgpt/tool-presentation";
import {knownToolActivity} from "@repo/ui/gailgpt/tool-display";
import {extractToolName} from "@repo/ui/gailgpt/tool-calls";
import {IconNameExtended} from "@/components/library/Icon";
import {pylonSupportCompletedPhraseKey} from "@/utils/chat/ticketConfirmationChoices";
import {
   type TranslateFn,
   isCodeExecutionTool,
   getCodeExecutionKind,
   isFailedToolResult,
   parseToolResult,
   inferCommandSummary,
   inferPythonSummary,
   extractShellCommand,
   extractPythonScript,
   extractCodePayload,
   truncateText,
   normalizeWhitespace,
} from "./toolCallUtils";

export type ToolConfig = {
   icon: IconNameExtended;
   inProgressText: string;
   completedText: string;
   completedGroupText?: string;
   countTextKey?: string;
   subtitle?: string;
};

const getPythonExecutionConfig = (
   t: TranslateFn,
   failed: boolean,
   parameters?: Record<string, unknown>
): ToolConfig => {
   const script = extractPythonScript(parameters);

   return {
      icon: "terminal",
      inProgressText: t("tools.phrases.runningPython"),
      completedText: failed ? t("tools.phrases.pythonFailed") : t("tools.phrases.ranPython"),
      completedGroupText: t("tools.phrases.ranPython"),
      countTextKey: "tools.phrases.stepCount",
      subtitle: !failed && script ? inferPythonSummary(script, t) : undefined,
   };
};

const getCodePayloadConfig = (
   t: TranslateFn,
   failed: boolean,
   parameters?: Record<string, unknown>
): ToolConfig => {
   const script = extractCodePayload(parameters);
   const firstLine = script
      ? truncateText(normalizeWhitespace(script.split(/\r?\n/u)[0] ?? ""))
      : undefined;

   return {
      icon: "terminal",
      inProgressText: t("tools.phrases.runningCode"),
      completedText: failed ? t("tools.phrases.codeFailed") : t("tools.phrases.ranCode"),
      completedGroupText: t("tools.phrases.ranCode"),
      countTextKey: "tools.phrases.stepCount",
      subtitle: !failed && firstLine ? firstLine : undefined,
   };
};

const getShellExecutionConfig = (
   t: TranslateFn,
   failed: boolean,
   parameters?: Record<string, unknown>
): ToolConfig => {
   const command = extractShellCommand(parameters);

   return {
      icon: "terminal",
      inProgressText: t("tools.phrases.runningShellCommand"),
      completedText: failed ? t("tools.phrases.shellCommandFailed") : t("tools.phrases.ranShellCommand"),
      completedGroupText: t("tools.phrases.ranShellCommands"),
      countTextKey: "tools.phrases.stepCount",
      subtitle: !failed && command ? inferCommandSummary(command, t) : undefined,
   };
};

const getCodeExecutionConfig = (
   functionName: string,
   t: TranslateFn,
   parameters?: Record<string, unknown>,
   result?: unknown
): ToolConfig => {
   const failed = isFailedToolResult(parseToolResult(result));
   const kind = getCodeExecutionKind(functionName, parameters);

   if (kind === "python") return getPythonExecutionConfig(t, failed, parameters);
   if (kind === "code") return getCodePayloadConfig(t, failed, parameters);
   return getShellExecutionConfig(t, failed, parameters);
};

const getWebSearchConfig = (t: TranslateFn, parameters?: Record<string, unknown>): ToolConfig => {
   const query = parameters?.query;
   const hasQuery = typeof query === "string" && query.trim().length > 0;
   return {
      icon: "globe",
      inProgressText: t("tools.phrases.searchingWeb"),
      completedText: t("tools.phrases.searchedWeb"),
      completedGroupText: t("tools.phrases.searchedWeb"),
      countTextKey: "tools.phrases.queryCount",
      subtitle: hasQuery ? t("tools.phrases.searchedQuery", {query}) : undefined,
   };
};

const getDocsSearchConfig = (t: TranslateFn, parameters?: Record<string, unknown>): ToolConfig => {
   const query = parameters?.query;
   const hasQuery = typeof query === "string" && query.trim().length > 0;
   return {
      icon: "magnifying-glass",
      inProgressText: t("tools.phrases.searchingDocs"),
      completedText: t("tools.phrases.searchedDocs"),
      completedGroupText: t("tools.phrases.searchedDocs"),
      countTextKey: "tools.phrases.queryCount",
      subtitle: hasQuery ? t("tools.phrases.searchedQuery", {query}) : undefined,
   };
};

const getDocsReadConfig = (t: TranslateFn, parameters?: Record<string, unknown>): ToolConfig => {
   const page = parameters?.page;
   const hasPage = typeof page === "string" && page.trim().length > 0;
   return {
      icon: "book",
      inProgressText: t("tools.phrases.readingDocs"),
      completedText: t("tools.phrases.readDocs"),
      completedGroupText: t("tools.phrases.readDocs"),
      countTextKey: "tools.phrases.stepCount",
      subtitle: hasPage ? t("tools.phrases.readDocsPage", {page}) : undefined,
   };
};

const PYLON_SUPPORT_TOOL_CONFIG = {
   find_my_tickets: {
      icon: "clipboard-list" as const,
      inProgressKey: "tools.phrases.checkingTickets",
      completedKey: "tools.phrases.checkedTickets",
   },
   get_ticket_status: {
      icon: "circle-info" as const,
      inProgressKey: "tools.phrases.checkingTicketStatus",
      completedKey: "tools.phrases.checkedTicketStatus",
   },
   create_support_ticket: {
      icon: "headset" as const,
      inProgressKey: "tools.phrases.filingTicket",
      completedKey: "tools.phrases.filedTicket",
   },
   add_ticket_comment: {
      icon: "comments" as const,
      inProgressKey: "tools.phrases.updatingTicket",
      completedKey: "tools.phrases.updatedTicket",
   },
} satisfies Record<string, Pick<ToolConfig, "icon"> & {inProgressKey: string; completedKey: string}>;

type PylonSupportToolName = keyof typeof PYLON_SUPPORT_TOOL_CONFIG;

const isPylonSupportToolName = (name: string): name is PylonSupportToolName =>
   Object.hasOwn(PYLON_SUPPORT_TOOL_CONFIG, name);

const getPylonSupportConfig = (
   functionName: PylonSupportToolName,
   t: TranslateFn,
   result?: unknown
): ToolConfig => {
   const spec = PYLON_SUPPORT_TOOL_CONFIG[functionName];
   const completedKey = pylonSupportCompletedPhraseKey(functionName, result) ?? spec.completedKey;
   return {
      icon: spec.icon,
      inProgressText: t(spec.inProgressKey),
      completedText: t(completedKey),
      completedGroupText: t(completedKey),
      countTextKey: "tools.phrases.stepCount",
   };
};

// Compiled Omega tool names are lowercase operation IDs, which do not humanize well.
const OMEGA_TOOL_PHRASE_KEYS: Record<string, string> = {
   omega_fetchallnodedefinitions: "readNodes",
   omega_fetchnodedefinition: "readNodes",
   omega_fetchallcustomerworkflows: "findWorkflows",
   omega_fetchcustomerworkflow: "readWorkflow",
   omega_checkcustomerworkflow: "checkWorkflow",
   omega_savedraftcustomerworkflow: "saveDraft",
   omega_fetchlastworkflowruns: "readRuns",
   omega_fetchworkflowrun: "readRuns",
   omega_startdebugsession: "startTest",
   omega_stepdebugsession: "stepTest",
   omega_rundebugsession: "runTest",
   omega_stopdebugsession: "stopTest",
};

const getOmegaToolConfig = (functionName: string, t: TranslateFn): ToolConfig => {
   const key = `tools.phrases.workflow.${OMEGA_TOOL_PHRASE_KEYS[functionName]}`;
   return {
      icon: "diagram-project",
      inProgressText: t(`${key}.inProgress`),
      completedText: t(`${key}.done`),
      completedGroupText: t(`${key}.done`),
      countTextKey: "tools.phrases.stepCount",
   };
};

const getSkillConfig = (t: TranslateFn, parameters?: Record<string, unknown>): ToolConfig => ({
   ...knownToolActivity("load_skill", parameters, t)!,
   countTextKey: "tools.phrases.stepCount",
});

const getFallbackConfig = (functionName: string, t: TranslateFn): ToolConfig => {
   const toolName = extractToolName(functionName);
   return {
      icon: "wrench",
      inProgressText: toolName
         ? t("tools.phrases.usingTool", {tool: toolName})
         : t("tools.phrases.usingGenericTool"),
      completedText: toolName
         ? t("tools.phrases.usedTool", {tool: toolName})
         : t("tools.phrases.usedGenericTool"),
      completedGroupText: toolName
         ? t("tools.phrases.usedTool", {tool: toolName})
         : t("tools.phrases.usedGenericTool"),
      countTextKey: "tools.phrases.stepCount",
   };
};

const toolMatchers: Array<{
   test: (name: string) => boolean;
   config: (name: string, t: TranslateFn, params?: Record<string, unknown>, result?: unknown) => ToolConfig;
}> = [
   {
      test: (name) => name.includes("WebSearchPlugin") || name.includes("web_search"),
      config: (_name, t, params) => getWebSearchConfig(t, params),
   },
   {
      test: (name) => name === "search_docs",
      config: (_name, t, params) => getDocsSearchConfig(t, params),
   },
   {
      test: (name) => name === "read_doc_page",
      config: (_name, t, params) => getDocsReadConfig(t, params),
   },
   {
      test: isPylonSupportToolName,
      config: (name, t, _params, result) =>
         getPylonSupportConfig(name as PylonSupportToolName, t, result),
   },
   {
      test: (name) => Object.hasOwn(OMEGA_TOOL_PHRASE_KEYS, name),
      config: (name, t) => getOmegaToolConfig(name, t),
   },
   {
      test: (name) => name.includes("document_query"),
      config: (_name, t) => ({
         icon: "file-magnifying-glass",
         inProgressText: t("tools.phrases.readingKnowledge"),
         completedText: t("tools.phrases.readKnowledge"),
         completedGroupText: t("tools.phrases.readKnowledge"),
         countTextKey: "tools.phrases.stepCount",
      }),
   },
   {
      test: (name) => isCodeExecutionTool(name),
      config: (name, t, params, result) => getCodeExecutionConfig(name, t, params, result),
   },
   {
      test: (name) => name.includes("SkillPlugin") || name.includes("SkillLoader") || name.includes("activate_skill") || name.includes("load_skill") || name === "load_gail_skill",
      config: (_name, t, params) => getSkillConfig(t, params),
   },
   {
      test: (name) => name.includes("calls_startcall") || name.includes("startCall"),
      config: (_name, t) => ({
         icon: "phone-arrow-right",
         inProgressText: t("tools.phrases.makingCall"),
         completedText: t("tools.phrases.madeCall"),
         completedGroupText: t("tools.phrases.madeCall"),
         countTextKey: "tools.phrases.callCount",
      }),
   },
];

export const getToolConfig = (
   functionName: string,
   t: TranslateFn,
   parameters?: Record<string, unknown>,
   result?: unknown,
   presentation?: ToolPresentation
): ToolConfig => {
   const known = knownToolActivity(functionName, parameters, t);
   if (known) return known;
   if (presentation) {
      const title = presentationText(presentation.title, t);
      return {icon: presentationIcons[presentation.icon], inProgressText: title, completedText: title};
   }
   const match = toolMatchers.find((m) => m.test(functionName));
   if (match) return match.config(functionName, t, parameters, result);
   return getFallbackConfig(functionName, t);
};
